import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Target, Eye, CheckCircle2, Users, Award, Globe, Building2, Wrench, ChevronRight } from "lucide-react";

const values = [
  {
    icon: Award,
    title: "Quality Excellence",
    description: "We deliver only the highest quality products and services that meet international standards."
  },
  {
    icon: Users,
    title: "Customer Focus",
    description: "Our customers' success is our success. We prioritize their needs in everything we do."
  },
  {
    icon: Globe,
    title: "Integrity",
    description: "We conduct business with honesty, transparency, and ethical standards at every level."
  },
  {
    icon: Building2,
    title: "Innovation",
    description: "We embrace new technologies and methods to stay ahead in the evolving industry."
  }
];

const milestones = [
  { year: "2008", title: "Company Founded", description: "MAK Group established with a vision for excellence in heavy equipment" },
  { year: "2012", title: "HayalogiX Launch", description: "Transport & Equipment division begins operations" },
  { year: "2016", title: "Bright Perfect", description: "Heavy Equipment company joins the group" },
  { year: "2020", title: "Spare Parts Division", description: "MAK Spare Parts Trading established" },
  { year: "2024", title: "Regional Leader", description: "Recognized as industry leader across the UAE" }
];

export const About = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gray-900 py-20 md:py-28" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#F5B800] font-semibold text-sm uppercase tracking-wider mb-4 block">
              About Us
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Your Trusted Partner in Heavy Equipment Solutions
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed">
              With over 15 years of experience, MAK Group has established itself as a leading provider of heavy equipment, transport, and spare parts solutions across the UAE.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 md:py-24" data-testid="company-overview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Serving Our Customers with Professionalism and Integrity
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Founded in 2008, MAK Group of Company has grown from a single enterprise into a diversified group serving the heavy equipment and machinery industry across the UAE and beyond.
                </p>
                <p>
                  Our journey began with a simple mission: to provide reliable, high-quality equipment solutions to businesses in need. Today, we operate three specialized divisions, each dedicated to a specific aspect of the industry.
                </p>
                <p>
                  With a team of over 50 experts and a fleet of more than 100 equipment units, we have successfully served over 500 clients, building lasting relationships based on trust and excellence.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gray-50 p-8 text-center">
                  <div className="font-heading text-4xl font-bold text-[#F5B800] mb-2">15+</div>
                  <div className="text-gray-600 text-sm">Years Experience</div>
                </div>
                <div className="bg-gray-50 p-8 text-center">
                  <div className="font-heading text-4xl font-bold text-[#F5B800] mb-2">500+</div>
                  <div className="text-gray-600 text-sm">Clients Served</div>
                </div>
                <div className="bg-gray-50 p-8 text-center">
                  <div className="font-heading text-4xl font-bold text-[#F5B800] mb-2">3</div>
                  <div className="text-gray-600 text-sm">Companies</div>
                </div>
                <div className="bg-gray-50 p-8 text-center">
                  <div className="font-heading text-4xl font-bold text-[#F5B800] mb-2">50+</div>
                  <div className="text-gray-600 text-sm">Team Members</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 md:py-24 bg-gray-50" data-testid="vision-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 md:p-10 shadow-sm border-l-4 border-[#F5B800]"
            >
              <div className="w-14 h-14 bg-[#F5B800]/10 flex items-center justify-center mb-6">
                <Eye size={28} className="text-[#F5B800]" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-4">
                Our Vision
              </h3>
              <p className="text-gray-600 leading-relaxed">
                To be the most trusted and innovative leader in heavy equipment solutions, setting industry standards for quality, reliability, and customer service across the region and beyond.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#F5B800] p-8 md:p-10"
            >
              <div className="w-14 h-14 bg-black/10 flex items-center justify-center mb-6">
                <Target size={28} className="text-black" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-black mb-4">
                Our Mission
              </h3>
              <p className="text-black/80 leading-relaxed">
                To deliver exceptional value through comprehensive heavy equipment, transport, and spare parts solutions while maintaining the highest standards of safety, quality, and environmental responsibility.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 md:py-24" data-testid="core-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do at MAK Group.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-6 hover:shadow-md transition-shadow"
                data-testid={`value-card-${index}`}
              >
                <div className="w-12 h-12 bg-[#F5B800] flex items-center justify-center mb-4">
                  <value.icon size={24} className="text-black" />
                </div>
                <h3 className="font-heading text-lg font-bold text-gray-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 md:py-24 bg-gray-900" data-testid="timeline">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Our Journey
            </h2>
          </div>

          <div className="space-y-6">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-20 text-right">
                  <span className="font-heading text-2xl font-bold text-[#F5B800]">{milestone.year}</span>
                </div>
                <div className="w-3 h-3 bg-[#F5B800] rounded-full mt-3 flex-shrink-0" />
                <div className="bg-gray-800 p-6 flex-1">
                  <h3 className="font-heading text-lg font-bold text-white mb-2">
                    {milestone.title}
                  </h3>
                  <p className="text-gray-400 text-sm">{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#F5B800]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-black mb-4">
            Ready to Work Together?
          </h2>
          <p className="text-black/80 mb-8">
            Contact us today to discuss your heavy equipment needs.
          </p>
          <Link
            to="/companies#contact"
            data-testid="about-cta-btn"
            className="bg-black text-white hover:bg-gray-900 font-bold px-10 py-4 transition-all inline-flex items-center gap-2"
          >
            Get in Touch
            <ChevronRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};
